import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { automationLevelSchema } from '@rossum/api-client/queues';
import { Workspace } from '@rossum/api-client/workspaces';
import {
  FilterMultiSelect,
  FilterNumberValues,
} from '@rossum/components/filtering/components';
import { createSettings } from '@rossum/components/filtering/helpers';
import { EngineWithType } from './useAllEngines';

const AUTOMATION_LEVEL_OPTIONS = ['always', 'confident', 'never'] as const;

export const queueManagerQueryParser = z.object({
  workspaces: z.coerce
    .string()
    .transform(val => val.split(',').map(Number))
    .pipe(z.number().array())
    .optional()
    .catch(undefined),
  // Engine keys are compound strings like "aurora-5", "dedicated-3"
  engines: z.coerce
    .string()
    .transform(val => val.split(','))
    .pipe(z.string().array())
    .optional()
    .catch(undefined),
  extensions: z.coerce
    .string()
    .transform(val => val.split(',').map(Number))
    .pipe(z.number().array())
    .optional()
    .catch(undefined),
  automationLevel: z.coerce
    .string()
    .transform(val => val.split(','))
    .pipe(automationLevelSchema.array())
    .optional()
    .catch(undefined),
});

export type QueueManagerFilters = z.infer<typeof queueManagerQueryParser>;

type UseQueueManagerFilterSettingsProps = {
  workspaces: Workspace[] | undefined;
  isWorkspacesLoading: boolean;
  enginesFilterOptions: EngineWithType[];
  isEnginesLoading: boolean;
  setFilters: (filters: Partial<QueueManagerFilters>) => void;
};

export const useQueueManagerFilterSettings = ({
  workspaces,
  isWorkspacesLoading,
  enginesFilterOptions,
  isEnginesLoading,
  setFilters,
}: UseQueueManagerFilterSettingsProps) => {
  const intl = useIntl();

  const workspaceOptions = useMemo(
    () => workspaces?.map(w => w.id.toString()) ?? [],
    [workspaces]
  );

  const engineOptions = useMemo(
    () => enginesFilterOptions.map(e => e.key),
    [enginesFilterOptions]
  );

  return useMemo(() => {
    const getAutomationLevelLabel = (value: string) => {
      switch (value) {
        case 'always':
          return intl.formatMessage({
            id: 'features.queueManager.metaChips.automationLevel.always',
          });
        case 'confident':
          return intl.formatMessage({
            id: 'features.queueManager.metaChips.automationLevel.confident',
          });
        case 'never':
          return intl.formatMessage({
            id: 'features.queueManager.metaChips.automationLevel.never',
          });
        default:
          return value;
      }
    };

    return [
      createSettings({
        filterKey: 'workspaces',
        name: intl.formatMessage({
          id: 'features.queueManager.filters.workspaces',
        }),
        options: workspaceOptions,
        optionsAreLoading: isWorkspacesLoading,
        getOptionLabel: option =>
          workspaces?.find(w => w.id === Number(option))?.name ?? option,
        getChipLabel: values =>
          values
            .map(id => workspaces?.find(w => w.id === id)?.name ?? String(id))
            .join(', '),
        valueGetter: (currentValue: QueueManagerFilters) =>
          currentValue.workspaces,
        setFilter: value => setFilters({ workspaces: value }),
        Component: FilterMultiSelect,
      }),
      createSettings({
        filterKey: 'engines',
        name: intl.formatMessage({
          id: 'features.queueManager.filters.engines',
        }),
        options: engineOptions,
        optionsAreLoading: isEnginesLoading,
        getOptionLabel: option =>
          enginesFilterOptions.find(e => e.key === option)?.name ?? option,
        getChipLabel: values =>
          values
            .map(
              key => enginesFilterOptions.find(e => e.key === key)?.name ?? key
            )
            .join(', '),
        valueGetter: (currentValue: QueueManagerFilters) =>
          currentValue.engines,
        setFilter: value => setFilters({ engines: value }),
        Component: FilterMultiSelect,
      }),
      createSettings({
        filterKey: 'extensions',
        name: intl.formatMessage({
          id: 'features.queueManager.filters.extensions',
        }),
        options: [],
        getChipLabel: values => values.join(', '),
        valueGetter: (currentValue: QueueManagerFilters) =>
          currentValue.extensions,
        setFilter: value => setFilters({ extensions: value }),
        Component: FilterNumberValues,
      }),
      createSettings({
        filterKey: 'automationLevel',
        name: intl.formatMessage({
          id: 'features.queueManager.filters.automationLevel',
        }),
        options: [...AUTOMATION_LEVEL_OPTIONS],
        getOptionLabel: getAutomationLevelLabel,
        getChipLabel: values => values.map(getAutomationLevelLabel).join(', '),
        valueGetter: (currentValue: QueueManagerFilters) =>
          currentValue.automationLevel,
        setFilter: value => setFilters({ automationLevel: value }),
        Component: FilterMultiSelect,
      }),
    ];
  }, [
    intl,
    workspaceOptions,
    isWorkspacesLoading,
    workspaces,
    engineOptions,
    isEnginesLoading,
    enginesFilterOptions,
    setFilters,
  ]);
};
